import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { User, Mail, Shield } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export default function CurrentUserDisplay() {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
        <Shield className="w-4 h-4 text-yellow-600" />
        <span className="text-yellow-800">Using demo mode (not logged in)</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
      <div className="flex-shrink-0">
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.firstName}
            className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold shadow-sm">
            {user.firstName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <User className="w-3 h-3 text-gray-500" />
          <span className="text-sm font-medium text-gray-900 truncate">
            {user.firstName} {user.lastName}
          </span>
          <Badge variant="outline" className="text-xs">
            {user.role}
          </Badge>
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <Mail className="w-3 h-3 text-gray-400" />
          <span className="text-xs text-gray-600 truncate">{user.email}</span>
        </div>
      </div>

      <div className="flex-shrink-0">
        <div className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
          {user.provider === 'google' ? 'Google' : 'Demo'}
        </div>
      </div>
    </div>
  );
}

// Compact version for header
export function CurrentUserCompact() {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return <Badge variant="secondary">Demo Mode</Badge>;
  }

  return (
    <div className="flex items-center gap-2">
      {user.picture ? (
        <img
          src={user.picture}
          alt={user.firstName}
          className="w-6 h-6 rounded-full border border-gray-200"
        />
      ) : (
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
          {user.firstName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
        </div>
      )}
      <span className="text-sm font-medium text-gray-700 truncate max-w-[150px]">
        {user.firstName || user.email?.split('@')[0]}
      </span>
    </div>
  );
}

